import { useState } from 'react';
import { ChevronDown, Minus, Plus } from 'lucide-react';

// The count and order forms' items: a section per Nano Fibre category
// (meters first), each folding open and closed, each item with − / + and a
// number. `values` maps item id to what's typed ('' for nothing yet);
// `done(id)` lights an item green; `tally(items)` is the section's
// right-hand note and `whole(items)` turns it green.
// Nano Fibre items switched on for the NU app (04/11/2026) keep their own
// category; NFUK sends its civil items as 'civils' so they're never meters.
const GROUPS = [
  { id: 'civil', label: 'Meters' },
  { id: 'consumables', label: 'Consumables' },
  { id: 'civils', label: 'Civils' },
  { id: 'tools', label: 'Tools' },
  { id: 'uniform_ppe', label: 'Uniform & PPE' },
];
// The job card's items can carry the office's own `section` (Stock > Job card
// stock organizer): those sections come first, in the order the items are
// sent; anything not placed falls back to its category after them.
export function groupItems(items) {
  const own = [];
  for (const i of items) {
    if (!i.section) continue;
    const g = own.find((s) => s.sectionId === i.section.id);
    if (g) g.items.push(i);
    else own.push({ id: `s${i.section.id}`, sectionId: i.section.id, label: i.section.name, items: [i] });
  }
  const rest = items.filter((i) => !i.section);
  const known = GROUPS.map((g) => ({ ...g, items: rest.filter((i) => i.category === g.id) }));
  const other = rest.filter((i) => !GROUPS.some((g) => g.id === i.category));
  return [...own, ...known, { id: 'other', label: 'Other', items: other }].filter((g) => g.items.length);
}

export const digits = (v) => String(v).replace(/\D/g, '').slice(0, 5);

export default function ItemGroups({ items, values, onChange, done, tally, whole, verb = 'How many' }) {
  const [closed, setClosed] = useState({}); // sections folded away, by id
  const step = (id, d) => onChange(id, String(Math.max(0, (Number(values[id]) || 0) + d)));
  return (
    <div className="stock-items">
      {groupItems(items).map((g) => (
        <section key={g.id} className="stock-group" aria-label={g.label}>
          <h2 className="stock-group-head">
            <button type="button" aria-expanded={!closed[g.id]} aria-controls={`grp-${g.id}`} onClick={() => setClosed({ ...closed, [g.id]: !closed[g.id] })}>
              <ChevronDown size={20} aria-hidden="true" className={closed[g.id] ? 'folded' : ''} />
              <span>{g.label}</span>
              <span className={`small stock-group-n${whole(g.items) ? ' ok' : ''}`}>{tally(g.items)}</span>
            </button>
          </h2>
          <div id={`grp-${g.id}`} className="stock-group-items" hidden={Boolean(closed[g.id])}>
            {g.items.map((i) => (
              <div key={i.id} className={`stock-row${done(i.id) ? ' filled' : ''}`}>
                <label htmlFor={`q-${i.id}`}>
                  <strong>{i.description}{i.required && <span className="req-tag">Required</span>}</strong>
                  <span className="small muted">{i.sku}</span>
                  {i.pack && <span className="small pack-note">Count single {i.pack.unit} (it comes in packs of {i.pack.size})</span>}
                </label>
                <div className="stepper">
                  <button type="button" className="stock-step" aria-label={`One less ${i.description}`} onClick={() => step(i.id, -1)}><Minus size={16} aria-hidden="true" /></button>
                  <input id={`q-${i.id}`} inputMode="numeric" pattern="[0-9]*" value={values[i.id] ?? ''} onChange={(e) => onChange(i.id, digits(e.target.value))} placeholder="–" aria-label={`${verb} ${i.description}`} />
                  <button type="button" className="stock-step plus" aria-label={`One more ${i.description}`} onClick={() => step(i.id, 1)}><Plus size={16} aria-hidden="true" /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
